import NumberPicker from '@/components/NumberPicker'
import PreviewActionBar from '@/components/PreviewActionBar'
import PreviewStatusChip, { type PreviewStatus } from '@/components/PreviewStatusChip'
import PreviewUnavailable from '@/components/PreviewUnavailable'
import appVariables from '@/config'
import {
  type BasketItemFragment,
  useGetDraftMenuItemLazyQuery,
  useGetLiveMenuItemLazyQuery,
} from '@/generated/graphql'
import { formatTaxRatePercent } from '@/helpers/vatRegions'
import { mobileMediaQuery } from '@caterdesk/ui--theme'
import { calculateItemSubtotal } from '@caterdesk/utils--basket'
import {
  getInitiallySelectedOptionItems,
  reachedMinimumOptionRequirements,
  type SelectedOptionItem as SelectedOptionItemValidation,
} from '@caterdesk/utils--gm-validation'
import { amountToPriceString } from '@caterdesk/utils--money'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import { Star } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Tab as MuiTab,
  Stack,
  Tabs,
  Typography,
} from '@mui/material'

import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia } from 'react-use'
import { match } from 'ts-pattern'

import { useAppStateStore } from '../../../common/store/appStateStore'
import { createMenuItems, type MenuItem, type PriceAudience } from '../../domain/menu-item'
import type { MenuItemOption, MenuOptionItem } from '../../domain/menu-item-option'
import type { MenuVendor } from '../../domain/menu-vendor'
import type { AddItemPayload } from '../../states/menu-page/add-item-mutation'
import type { UpdateBasketItemPayload } from '../../states/menu-page/update-item-mutation'
import { getMenuItemTranslatedValue } from '../../utils/getMenuItemTranslatedValue'
import { IngredientsTab } from './ingredients-tab'
import { NutritionalTab } from './nutritional-tab'
import { OverviewTab } from './overview-tab'
import { SourcingTab } from './sourcing-tab'

export type MenuItemModalVendor = Pick<MenuVendor, 'currency' | 'companyName' | 'sourcing' | 'logo'>

export type Props = (
  | { mode: 'add'; onConfirmClick: (item: AddItemPayload) => void }
  | { mode: 'edit'; onConfirmClick: (item: UpdateBasketItemPayload) => void }
  | {
      mode: 'preview'
      itemSource: 'live' | 'draft'
      status: PreviewStatus
      // Required in preview (overrides the optional common prop): without an id
      // the lazy query never fires and the loading dialog would spin forever.
      menuItemReference: string
      onDuplicateClick?: () => void
      onEditClick: () => void
      onDeleteClick?: () => void
    }
) & {
  menuItem?: MenuItem
  menuItemReference?: string
  basketItem?: BasketItemFragment
  vendor: MenuItemModalVendor
  onClose: () => void
  taxAcronym: string
  initialQty?: number
  accountName?: string | undefined
  /** Price basis for every price in the modal. Defaults to 'customer'
      (marketplace); the admin menu-list preview passes 'vendor' so prices
      match the list cards. */
  priceAudience?: PriceAudience
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ingredients', label: 'Ingredients' },
  { id: 'nutritional', label: 'Nutritional' },
  { id: 'sourcing', label: 'Sourcing' },
] as const
type Tab = (typeof TABS)[number]

export type SelectedOptionItem = {
  option: MenuItemOption
  item: MenuOptionItem
  quantity: number
}

export const MenuItemModal: React.FC<Props> = (props) => {
  const { t, i18n } = useTranslation()
  const { menuItem, menuItemReference, basketItem, vendor, initialQty, accountName, onClose } =
    props
  const isMobileScreen = useMedia(mobileMediaQuery, false)
  const [activeTabId, setActiveTabId] = useState<Tab['id']>('overview')
  const [quantity, setQuantity] = useState(initialQty || basketItem?.qty || menuItem?.minQty || 1)
  const [note, setNote] = useState(basketItem?.note || '')
  const [loading, setLoading] = useState(false)

  const isDraftSource = props.mode === 'preview' && props.itemSource === 'draft'
  // Both hooks must mount unconditionally (hooks rules); only one ever runs.
  const [runLoadLive, liveQuery] = useGetLiveMenuItemLazyQuery()
  const [runLoadDraft, draftQuery] = useGetDraftMenuItemLazyQuery()
  const { fetchedItem, called, queryLoading, queryError } = isDraftSource
    ? {
        fetchedItem: draftQuery.data?.draftMenuItem,
        called: draftQuery.called,
        queryLoading: draftQuery.loading,
        queryError: draftQuery.error,
      }
    : {
        fetchedItem: liveQuery.data?.liveMenuItem,
        called: liveQuery.called,
        queryLoading: liveQuery.loading,
        queryError: liveQuery.error,
      }

  // Refetch full option data when the list payload is incomplete
  const needsFreshData =
    menuItemReference ||
    menuItem?.options === null ||
    (menuItem?.options?.some(
      (opt) =>
        opt &&
        ((opt.items?.length ?? 0) === 0 ||
          (opt.items?.some((optItem) => optItem && !('customerPriceItems' in optItem)) ?? false)),
    ) ??
      false)
  const menuItemId = menuItemReference || menuItem?._id

  useEffect(() => {
    if (needsFreshData && menuItemId && !called) {
      const runLoadMenuData = isDraftSource ? runLoadDraft : runLoadLive
      runLoadMenuData({
        variables: {
          id: menuItemId,
        },
      })
    }
  }, [needsFreshData, menuItemId, called, isDraftSource, runLoadDraft, runLoadLive])

  const basketDeliveryDate = useAppStateStore((state) => state.userState.date)
  const optionReferenceDate = useMemo(
    () => (basketDeliveryDate ? new Date(basketDeliveryDate) : new Date()),
    [basketDeliveryDate],
  )
  const menuItemData =
    fetchedItem && needsFreshData
      ? createMenuItems([fetchedItem], optionReferenceDate)[0]
      : menuItem

  const isLoadingFullOptions = Boolean(needsFreshData) && !fetchedItem

  const menuItemWithOptions = {
    options: menuItemData?.options as unknown as SelectedOptionItemValidation['option'][],
  }
  const [selectedOptions, setSelectedOptions] = useState<
    Map<MenuItemOption['_id'], Map<MenuOptionItem['_id'], SelectedOptionItem>>
  >(basketItem ? getInitiallySelectedOptionItems(menuItemWithOptions, basketItem) : new Map())
  const confirmButtonCopy = basketItem
    ? t('marketplace:updateBasket')
    : t('marketplace:addToBasket')
  const taxCopy = `(${t('marketplace:ex', { taxAcronym: props.taxAcronym })})`
  const options = [...selectedOptions.values()]
    .flatMap((o) => [...o.values()])
    .map(({ option, item, quantity: optionQty }) => ({
      price: item.price || 0,
      priceExTax: item.priceExTax || 0,
      qty: option.optionType === 'ticks' ? quantity : optionQty,
      type: option.optionType || undefined,
      item: item.item,
    }))

  const itemSubtotals = calculateItemSubtotal(
    {
      itemPrice: menuItemData?.price ?? 0,
      itemPriceExTax: menuItemData?.priceExTax ?? 0,
      itemTaxRate: menuItemData?.taxRate ?? 0,
      itemTaxValue: menuItemData?.taxValue ?? 0,
    },
    quantity,
    options,
  )

  const total = itemSubtotals.subtotalExTax
  const locale = i18n.language?.split('-')[0]?.toLowerCase()
  const translatedMenuItem = useMemo(() => {
    if (!menuItemData) return undefined

    const name = getMenuItemTranslatedValue({
      translations: menuItemData.translations,
      locale,
      key: 'name',
      fallback: menuItemData.name,
    })
    const description = getMenuItemTranslatedValue({
      translations: menuItemData.translations,
      locale,
      key: 'description',
      fallback: menuItemData.description,
    })
    const shortDescription = getMenuItemTranslatedValue({
      translations: menuItemData.translations,
      locale,
      key: 'shortDescription',
      fallback: menuItemData.shortDescription,
    })

    return {
      ...menuItemData,
      name,
      description,
      shortDescription,
    }
  }, [locale, menuItemData])

  const handleAddItem = () => {
    if (!translatedMenuItem) return
    setLoading(true)

    const options = [...selectedOptions.values()]
      .flatMap((o) => [...o.values()])
      .map(({ option, item, quantity }) => ({
        option: {
          ...option,
          // @ts-expect-error qty is not defined for ticks options for some reason?
          qty: option.optionType === 'ticks' ? quantity : option.qty,
        },
        optionItem: item,
        qty: quantity,
      }))
    match(props)
      .with({ mode: 'add' }, ({ onConfirmClick }) =>
        onConfirmClick({ item: translatedMenuItem, qty: quantity, note, options }),
      )
      .with({ mode: 'edit' }, ({ onConfirmClick }) => onConfirmClick({ qty: quantity, options }))
      .with({ mode: 'preview' }, () => {})
      .exhaustive()

    onClose()
  }

  const selectOptionItem = (option: MenuItemOption, item: MenuOptionItem, optionQty?: number) => {
    const optionId = option._id
    const optionItemId = item._id
    const options = new Map(selectedOptions)
    const selectedOptionGroup = options.get(optionId)

    if (optionQty === 0) {
      if (selectedOptionGroup) {
        const option = new Map(selectedOptionGroup)
        option.delete(optionItemId)
        options.set(optionId, option)
        if (option.size === 0) {
          options.delete(optionId)
        }
      }
    } else {
      const selectedOption: Map<string, SelectedOptionItem> = selectedOptionGroup
        ? new Map(selectedOptionGroup)
        : new Map()

      if (selectedOptionGroup) {
        if (optionQty) {
          selectedOption.set(optionItemId, { option, item, quantity: optionQty })
        } else {
          if (selectedOption.has(optionItemId)) {
            selectedOption.delete(optionItemId)
          } else {
            selectedOption.set(optionItemId, {
              option,
              item,
              quantity: option.optionType === 'ticks' ? quantity : 1,
            })
          }
        }
      } else {
        selectedOption.set(optionItemId, { option, item, quantity: optionQty ?? quantity })
      }

      options.set(optionId, selectedOption)
    }
    setSelectedOptions(options)
  }

  const reachedMinOptionRequirementsHandler = useMemo(() => {
    return reachedMinimumOptionRequirements({
      menuItem: {
        options: translatedMenuItem?.options as unknown as SelectedOptionItemValidation['option'][],
      },
      selectedOptions: selectedOptions as unknown as Map<
        string,
        Map<string, SelectedOptionItemValidation>
      >,
      topQuantity: quantity,
    })
  }, [selectedOptions, quantity, translatedMenuItem?.options])

  if (!translatedMenuItem) {
    // A preview opened by reference has no fallback item to render while the fetch resolves
    if (props.mode !== 'preview') return null

    // We're already inside !translatedMenuItem — once the query has settled (not loading), or
    // it errored outright (e.g. item deleted mid-flight), there's genuinely nothing to show
    const queryFinishedWithNothing = (called && !queryLoading) || !!queryError

    return (
      // Same Box > Dialog shape (and Dialog props) as the loaded return below:
      // React then reconciles the two trees instead of remounting the Dialog,
      // so the open transition doesn't re-run when loading resolves (the
      // remount showed as a modal "flash" on fast fetches).
      <Box sx={{ zIndex: 1300, position: 'relative' }}>
        <Dialog
          open={true}
          fullWidth
          maxWidth="lg"
          onClose={onClose}
          fullScreen={isMobileScreen}
          sx={{
            '.MuiPaper-root': {
              height: { xs: '100%', sm: '60%' },
              minHeight: '500px',
              borderRadius: { xs: 0, sm: 4 },
              padding: 2,
            },
          }}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}>
            <CloseIcon />
          </IconButton>
          <Stack alignItems="center" justifyContent="center" flex={1} gap={2} px={4}>
            {queryFinishedWithNothing ? (
              <PreviewUnavailable detail="It may not have marketplace data (e.g. a canteen-only item) or may have been deleted." />
            ) : (
              <CircularProgress />
            )}
          </Stack>
        </Dialog>
      </Box>
    )
  }

  const previewUsesIncTax =
    props.priceAudience === 'vendor' && translatedMenuItem.vendorPriceExTax == null
  const previewPrice =
    props.priceAudience === 'vendor'
      ? (translatedMenuItem.vendorPriceExTax ?? translatedMenuItem.vendorPrice)
      : translatedMenuItem.priceExTax

  const tabContents: Record<Tab['id'], React.ReactElement> = {
    overview: isLoadingFullOptions ? (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
        <CircularProgress />
      </Stack>
    ) : (
      <OverviewTab
        taxAcronym={props.taxAcronym}
        vendor={vendor}
        item={translatedMenuItem}
        quantity={quantity}
        selectedOptions={selectedOptions}
        selectOptionItem={selectOptionItem}
        onAllergyButtonClick={() => setActiveTabId('ingredients')}
        note={note}
        noteIsEditable={!basketItem}
        setNote={setNote}
        showNotes={props.mode !== 'preview'}
        pricesIncTax={false}
        priceAudience={props.priceAudience}
      />
    ),
    ingredients: <IngredientsTab vendor={vendor} item={translatedMenuItem} />,
    nutritional: <NutritionalTab vendor={vendor} item={translatedMenuItem} />,
    sourcing: <SourcingTab vendor={vendor} />,
  }

  let servesAmount = (translatedMenuItem?.servings ?? 0) * quantity || 0
  if (translatedMenuItem?.isPricePerHead) servesAmount = quantity

  const bgImage = translatedMenuItem?.image
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: translatedMenuItem.image,
        width: 600,
        quality: 100,
      })
    : null

  const setItemQuantity = (quantity: number) => {
    setQuantity(quantity)

    const options = new Map(selectedOptions)

    options.forEach((option) => {
      option.forEach((item) => {
        if (item.option.optionType === 'ticks') {
          item.quantity = quantity
        }
      })
    })

    setSelectedOptions(options)
  }

  return (
    <Box sx={{ zIndex: 1300, position: 'relative' }}>
      <Dialog
        open={true}
        fullWidth
        maxWidth="lg"
        onClose={onClose}
        fullScreen={isMobileScreen}
        sx={{
          '.MuiPaper-root': {
            height: { xs: '100%', sm: '60%' },
            minHeight: '500px',
            borderRadius: { xs: 0, sm: 4 },
            padding: 2,
          },
        }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <CloseIcon />
        </IconButton>
        {isMobileScreen && (
          <Stack
            direction="row"
            gap={2}
            justifyContent="space-between"
            py={1}
            px={3}
            alignItems="center">
            <DialogTitle sx={{ p: 0 }} />
            <IconButton color="inherit" onClick={onClose} aria-label="close" edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: bgImage ? '1fr 1fr' : '1fr',
            },
            overflow: 'hidden',
            height: '100%',
          }}>
          {bgImage && (
            <Box
              sx={{
                position: 'relative',
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 4,
              }}>
              {/* <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                position="absolute"
                bottom={0}
                p={2}
                width="100%"
                sx={{
                  background: 'linear-gradient(rgba(63, 65, 68, 0), rgba(63, 65, 68, 0.7))',
                  color: '#fff',
                }}
              >
                {vendor.logo && (
                  <CaptionLogo
                    src={getCloudflareImageUrl({ enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE, src: vendor.logo, width: 50, quality: 100 })}
                    alt="Vendor logo"
                  />
                )}
                <CaptionText>
                  {t('marketplace:freshlyPreparedBy', { companyName: vendor.companyName })}
                </CaptionText>
              </Stack> */}
            </Box>
          )}
          <Stack direction="column" overflow="hidden">
            {!isMobileScreen && (
              <Stack direction="row" alignItems="center" spacing={1} p={2} pb={0}>
                <Typography variant="h5">{translatedMenuItem.name}</Typography>
                {props.mode === 'preview' && <PreviewStatusChip status={props.status} />}
              </Stack>
            )}
            {translatedMenuItem.isPrivate && (
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Star sx={{ marginRight: 0.5 }} />
                    {/* Admin preview has no account context — the key would render a dangling "Private - " */}
                    {props.mode === 'preview'
                      ? 'Private'
                      : t('marketplace:private', { accountName })}
                  </Box>
                }
                sx={{
                  position: 'absolute',
                  top: 20,
                  left: '20%',
                  transform: 'translateX(-20%)',
                  backgroundColor: 'primary.light',
                }}
                color="primary"
                size="medium"
              />
            )}
            <Box
              sx={{
                overflowY: 'auto',
                flex: '1 1 0%',
                paddingLeft: 2,
              }}>
              <Box
                sx={{
                  position: 'sticky',
                  width: '100%',
                  top: 0,
                  zIndex: 1,
                  overflow: 'auto',
                  backgroundColor: '#fff',
                }}>
                <Tabs
                  value={activeTabId}
                  onChange={(_, value) => setActiveTabId(value)}
                  variant="fullWidth">
                  {TABS.map((tab) => (
                    <MuiTab key={tab.id} label={t(`marketplace:${tab.id}`)} value={tab.id} />
                  ))}
                </Tabs>
              </Box>
              {tabContents[activeTabId]}
            </Box>
            <Box
              sx={{
                p: 2,
                boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 30px',
              }}>
              {props.mode === 'preview' ? (
                <Stack direction="row" gap={2} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                    {amountToPriceString(vendor.currency, previewPrice, true)}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {` ${previewUsesIncTax ? 'inc' : 'ex'} ${translatedMenuItem.taxRate != null ? `${formatTaxRatePercent(translatedMenuItem.taxRate)} ` : ''}${props.taxAcronym}`}
                    </Typography>
                  </Typography>
                  <PreviewActionBar
                    onDuplicate={props.onDuplicateClick}
                    onEdit={props.onEditClick}
                    onDelete={props.onDeleteClick}
                  />
                </Stack>
              ) : (
                <Stack direction="row" gap={2}>
                  <NumberPicker
                    value={quantity}
                    min={translatedMenuItem.minQty}
                    max={2000}
                    onChange={setItemQuantity}
                  />
                  {match(isMobileScreen)
                    .with(true, () => (
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={!reachedMinOptionRequirementsHandler}
                        onClick={handleAddItem}
                        loading={loading}>
                        {confirmButtonCopy} ({amountToPriceString(vendor.currency, total, true)}){' '}
                        {taxCopy}
                      </Button>
                    ))
                    .with(false, () => (
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!reachedMinOptionRequirementsHandler}
                        onClick={handleAddItem}
                        loading={loading}>
                        <Stack direction="row" justifyContent="center" gap={1} flexWrap="wrap">
                          <span>{confirmButtonCopy}</span>

                          <span>
                            {amountToPriceString(vendor.currency, total, true)} {taxCopy} (
                            {t('marketplace:serves', { servesAmount })})
                          </span>
                        </Stack>
                      </Button>
                    ))
                    .exhaustive()}
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  )
}
